import { isAxiosError } from 'axios';
import { toaster } from '@/components/ui/toaster';
import type { useSharingMutations } from '@/hooks/sharing/useSharingMutations';
import type { AssignableRole } from '@/utils/roleUtils';

/**
 * A filled-in invite form, as the dialog hands it over.
 *
 * `expiresAt` is a Date rather than the ISO string the API wants, because the picker works in
 * dates and every flow serialises it the same way.
 */
export interface AccessGrantInput {
	email: string;
	name?: string;
	purpose?: string;
	message?: string;
	role: AssignableRole;
	/** The code of the role the new contact card is filed under. Absent where no card is filed. */
	contactRole?: string;
	expiresAt: Date | null;
}

/**
 * What a dialog does once its form is filled.
 *
 * The invite dialog is one form with two meanings: on the Access page it adds somebody and tells
 * them, and on the contacts page it does that and files them as a service contact too. The form,
 * its validation and its copy are the dialog's; the writes are the flow's.
 *
 * `submit` resolves false when the grant did not land, which is what keeps the form open with
 * what the reader typed still in it. A flow reports its own failures — the dialog has no way to
 * describe a write it does not know about.
 */
export interface AccessGrantFlow {
	submit: (input: AccessGrantInput) => Promise<boolean>;
	isPending: boolean;
	/**
	 * Whether the dialog must ask which role to file this address under. Absent means never: a flow
	 * that starts from a card has one already.
	 */
	asksContactRole?: (email: string) => boolean;
}

/**
 * The invitation every flow sends once its grant has landed.
 *
 * The access stands whatever happens here, so a failure is reported as the email's alone, under
 * `grantedTitle`. A 501 means this environment has no email path, which the reader cannot act on,
 * so it is only logged.
 */
export const sendInvitationAfterGrant = async (
	inviteUser: ReturnType<typeof useSharingMutations>['inviteUser'],
	path: { house_id: number; user_id: number },
	grantedTitle: string,
): Promise<void> => {
	try {
		await inviteUser.mutateAsync({ path });
	} catch (error) {
		console.error('Invitation failed after the grant:', error);
		if (isAxiosError(error) && error.response?.status === 501) return;
		toaster.create({ title: `${grantedTitle}, but the invitation email was not sent`, type: 'error' });
	}
};
